import React from 'react';
/**
 * Registry of all custom tabs.
 * To add a new custom tab:
 * 1. Create a TSX component in this folder (e.g. MyFeatureTab.tsx)
 * 2. Import it here
 * 3. Add an entry to the customTabs array with the watchface IDs it should appear for
 */
import BodySurfTab from './BodySurfTab';
import RadioCodeTab from './RadioCodeTab';
import AirQualityViewsTab from './AirQualityViewsTab';
import AirQualityControlsTab from './AirQualityControlsTab';
import AirQualityIndexTab from './AirQualityIndexTab';
import AirQualityValuesTab from './AirQualityValuesTab';
import AirQualitySettingsTab from './AirQualitySettingsTab';
import FirmamentTab from './FirmamentTab';
import VoidrunnerTab from './VoidrunnerTab';
import ContentTab from '../product/ContentTab';
import FeaturesTab from '../product/FeaturesTab';
const contentTab = (key, images) => {
    const Tab = () => React.createElement(ContentTab, { contentKey: key, images });
    Tab.displayName = `ContentTab(${key})`;
    return Tab;
};
/** Watchfaces mit dem neuen Template (G-Code): gemeinsamer Tab für Touch, Gesten und Anzeige */
const NEW_TEMPLATE_SLUGS = ['basicpro', 'nevada', 'tactical', 'senator', 'tomahawk', 'fletcher', 'iozen', 'vanguard', 'nightfever', 'blackhawk', 'zero'];
const AIR_QUALITY_MATCH = {
    watchfaceIds: [40],
    apiNames: ['AirQualityPro', 'Air Quality Pro', 'airqualitypro', 'AtmosAir', 'atmosair'],
    weblinks: ['airqualitypro', 'atmosair'],
};
function matchesProduct(tab, product) {
    if (tab.watchfaceIds != null && tab.watchfaceIds.includes(product.id)) {
        return true;
    }
    if (tab.apiNames != null && tab.apiNames.length > 0) {
        const api = (product.apiName || '').toLowerCase();
        const name = (product.name || '').toLowerCase();
        for (const apiName of tab.apiNames) {
            const key = apiName.toLowerCase();
            if (api === key || name === key) {
                return true;
            }
        }
    }
    if (tab.weblinks != null && tab.weblinks.length > 0) {
        const slug = (product.weblink || '').replace(/\/$/, '').toLowerCase();
        for (const link of tab.weblinks) {
            if (slug === link.replace(/\/$/, '').toLowerCase()) {
                return true;
            }
        }
    }
    return false;
}
export const customTabs = [
    {
        id: 'firmament-features',
        watchfaceIds: [42],
        apiNames: ['Firmament'],
        weblinks: ['firmament'],
        label: {
            en: 'Sky & features',
            de: 'Himmel & Funktionen',
            fr: 'Ciel et fonctions',
            es: 'Cielo y funciones',
            it: 'Cielo e funzioni',
            pt: 'Céu e funções',
            nl: 'Hemel en functies',
            pl: 'Niebo i funkcje',
            cs: 'Obloha a funkce',
            ja: '空と機能',
            ko: '하늘과 기능',
            zh: '天空与功能',
            ru: 'Небо и функции',
            uk: 'Небо та функції',
            no: 'Himmel og funksjoner',
            sv: 'Himmel och funktioner',
        },
        icon: '🌌',
        order: 3,
        component: FirmamentTab,
    },
    {
        id: 'horizon-views',
        watchfaceIds: [1],
        weblinks: ['horizon'],
        labelKey: 'ptabs.horizon.tabLabel',
        order: 4,
        component: contentTab('horizon', {
            moon: '/images/horizon/moonview.png',
            forecast: '/images/horizon/forecast.png',
            bodystatus: '/images/horizon/bodystatus.png',
            movegoals: '/images/horizon/movegoals.png',
            charts: '/images/horizon/charts.png',
            arcslots: '/images/horizon/arcslots.png',
            tides: '/images/horizon/tides.png',
            airquality: '/images/horizon/aq.png',
        }),
    },
    {
        id: 'zenith-planets',
        watchfaceIds: [9],
        weblinks: ['zenith'],
        labelKey: 'ptabs.zenith.tabLabel',
        order: 5,
        component: contentTab('zenith', { planets: '/images/zenith/PLANTS.png' }),
    },
    {
        id: 'centurion-views',
        watchfaceIds: [5],
        weblinks: ['centurion'],
        labelKey: 'ptabs.centurion.tabLabel',
        order: 6,
        component: contentTab('centurion', { charts: '/images/centurion/chart.png', weather: '/images/centurion/weather.png' }),
    },
    {
        id: 'mission-second-view',
        watchfaceIds: [2],
        weblinks: ['mission'],
        labelKey: 'ptabs.mission.tabLabel',
        order: 7,
        component: contentTab('mission', { exp: '/images/mission/exp.png' }),
    },
    {
        id: 'bierwandern-guide',
        watchfaceIds: [34],
        weblinks: ['bierwandern'],
        labelKey: 'ptabs.bierwandern.tabLabel',
        order: 3,
        component: contentTab('bierwandern'),
    },
    {
        id: 'newgen-controls',
        weblinks: NEW_TEMPLATE_SLUGS,
        labelKey: 'features.tabLabel',
        order: 3,
        component: FeaturesTab,
    },
    {
        id: 'morphius-views',
        weblinks: ['morphius'],
        labelKey: 'ptabs.morphius.tabLabel',
        order: 3,
        component: contentTab('morphius'),
    },
    {
        id: 'atmosweather-guide',
        watchfaceIds: [43],
        weblinks: ['atmosweather'],
        labelKey: 'ptabs.atmosweather.tabLabel',
        order: 3,
        component: contentTab('atmosweather'),
    },
    {
        id: 'voidrunner-guide',
        watchfaceIds: [46],
        apiNames: ['Voidrunner'],
        weblinks: ['voidrunner'],
        labelKey: 'ptabs.voidrunner.tabLabel',
        order: 3,
        component: VoidrunnerTab,
    },
    {
        id: 'bodysurf-guide',
        apiNames: ['BodySurf', 'BodySurfing'],
        label: {
            en: 'How to use',
            cs: 'Jak používat',
            de: 'Wie man es benutzt',
            nl: 'Hoe te gebruiken',
            fr: 'Comment utiliser',
            it: 'Come usare',
            ja: '使い方',
            ko: '사용 방법',
            no: 'Hvordan bruke',
            pl: 'Jak używać',
            pt: 'Como usar',
            ru: 'Как пользоваться',
            es: 'Cómo usar',
            sv: 'Hur man använder',
            uk: 'Як користуватися',
            zh: '如何使用',
        },
        icon: '🏄',
        order: 3,
        component: BodySurfTab,
    },
    {
        id: 'radiacode-guide',
        watchfaceIds: [41],
        apiNames: [
            'radiacodeapp',
            'RadioCode',
            'RadiaCode',
            'Radia Code App',
            'RadiaCode App',
            'RadiaCode Expedition',
            'radiacode',
        ],
        weblinks: ['radiacodeapp', 'radiacode'],
        label: {
            en: 'How to use',
            cs: 'Jak používat',
            de: 'Wie man es benutzt',
            nl: 'Hoe te gebruiken',
            fr: 'Comment utiliser',
            it: 'Come usare',
            ja: '使い方',
            ko: '사용 방법',
            no: 'Hvordan bruke',
            pl: 'Jak używać',
            pt: 'Como usar',
            ru: 'Как пользоваться',
            es: 'Cómo usar',
            sv: 'Hur man använder',
            uk: 'Як користуватися',
            zh: '如何使用',
        },
        icon: '☢️',
        order: 3,
        component: RadioCodeTab,
    },
    {
        id: 'aqpro-views',
        ...AIR_QUALITY_MATCH,
        label: {
            en: 'Views',
            de: 'Ansichten',
            fr: 'Vues',
            es: 'Vistas',
            it: 'Schermate',
            pt: 'Ecrãs',
            nl: 'Schermen',
            pl: 'Widoki',
            cs: 'Zobrazení',
            ja: '画面',
            ko: '화면',
            zh: '界面',
            ru: 'Экраны',
            uk: 'Екрани',
            no: 'Visninger',
            sv: 'Vyer',
        },
        icon: '🌬️',
        order: 3,
        component: AirQualityViewsTab,
    },
    {
        id: 'aqpro-controls',
        ...AIR_QUALITY_MATCH,
        label: {
            en: 'Controls',
            de: 'Bedienung',
            fr: 'Commandes',
            es: 'Controles',
            it: 'Comandi',
            pt: 'Controlos',
            nl: 'Bediening',
            pl: 'Sterowanie',
            cs: 'Ovládání',
            ja: '操作',
            ko: '조작',
            zh: '操作',
            ru: 'Управление',
            uk: 'Керування',
            no: 'Betjening',
            sv: 'Kontroller',
        },
        icon: '👆',
        order: 3.1,
        component: AirQualityControlsTab,
    },
    {
        id: 'aqpro-index',
        ...AIR_QUALITY_MATCH,
        label: {
            en: 'EU vs US index',
            de: 'EU- vs. US-Index',
            fr: 'Index UE / US',
            es: 'Índice UE / EE.UU.',
            it: 'Indice UE / USA',
            pt: 'Índice UE / EUA',
            nl: 'EU- vs. VS-index',
            pl: 'Indeks UE / USA',
            cs: 'Index EU / USA',
            ja: 'EUとUS指数',
            ko: 'EU vs US 지수',
            zh: '欧盟与美国指数',
            ru: 'Индекс ЕС / США',
            uk: 'Індекс ЄС / США',
            no: 'EU- vs. US-indeks',
            sv: 'EU- vs. US-index',
        },
        icon: '⚖️',
        order: 3.2,
        component: AirQualityIndexTab,
    },
    {
        id: 'aqpro-values',
        ...AIR_QUALITY_MATCH,
        label: {
            en: 'Values explained',
            de: 'Werte erklärt',
            fr: 'Valeurs expliquées',
            es: 'Valores explicados',
            it: 'Valori spiegati',
            pt: 'Valores explicados',
            nl: 'Waarden uitgelegd',
            pl: 'Wartości wyjaśnione',
            cs: 'Vysvětlení hodnot',
            ja: '値の説明',
            ko: '수치 설명',
            zh: '数值说明',
            ru: 'Значения',
            uk: 'Значення',
            no: 'Verdier forklart',
            sv: 'Värden förklarade',
        },
        icon: '📊',
        order: 3.3,
        component: AirQualityValuesTab,
    },
    {
        id: 'aqpro-settings',
        ...AIR_QUALITY_MATCH,
        label: {
            en: 'Settings',
            de: 'Einstellungen',
            fr: 'Paramètres',
            es: 'Ajustes',
            it: 'Impostazioni',
            pt: 'Definições',
            nl: 'Instellingen',
            pl: 'Ustawienia',
            cs: 'Nastavení',
            ja: '設定',
            ko: '설정',
            zh: '设置',
            ru: 'Настройки',
            uk: 'Налаштування',
            no: 'Innstillinger',
            sv: 'Inställningar',
        },
        icon: '⚙️',
        order: 3.4,
        component: AirQualitySettingsTab,
    },
];
export function getCustomTabsForProduct(product) {
    return customTabs
        .filter(tab => matchesProduct(tab, product))
        .sort((a, b) => a.order - b.order);
}
